import * as React from 'react';
import {ChevronLeft, ChevronRight} from 'lucide-react';
import {cn} from '../lib/cn';
import {useCarouselContext} from './CarouselContext';

type CarouselControlProps = Omit<React.ComponentPropsWithoutRef<'button'>, 'children'> & {
  children?: React.ReactNode;
};

export const CarouselPrevious = React.forwardRef<HTMLButtonElement, CarouselControlProps>(
  ({className, children, onClick, ...props}, ref) => {
    const {canGoPrevious, goToPrevious} = useCarouselContext('CarouselPrevious');

    if (!canGoPrevious) {
      return null;
    }

    return (
      <button
        ref={ref}
        type="button"
        data-slot="carousel-previous"
        aria-label="Previous slide"
        className={cn(
          `absolute left-0 top-1/2 flex size-8 -translate-y-1/2 translate-x-2 items-center
          justify-center rounded-full border-0 bg-[var(--carousel-control-bg)]
          text-[var(--carousel-control-color)]`,
          className,
        )}
        onClick={event => {
          onClick?.(event);
          goToPrevious();
        }}
        {...props}
      >
        {children ?? <ChevronLeft className="size-4" />}
      </button>
    );
  },
);

CarouselPrevious.displayName = 'CarouselPrevious';

export const CarouselNext = React.forwardRef<HTMLButtonElement, CarouselControlProps>(
  ({className, children, onClick, ...props}, ref) => {
    const {canGoNext, goToNext} = useCarouselContext('CarouselNext');

    if (!canGoNext) {
      return null;
    }

    return (
      <button
        ref={ref}
        type="button"
        data-slot="carousel-next"
        aria-label="Next slide"
        className={cn(
          `absolute right-0 top-1/2 flex size-8 -translate-y-1/2 -translate-x-2 items-center
          justify-center rounded-full border-0 bg-[var(--carousel-control-bg)]
          text-[var(--carousel-control-color)]`,
          className,
        )}
        onClick={event => {
          onClick?.(event);
          goToNext();
        }}
        {...props}
      >
        {children ?? <ChevronRight className="size-4" />}
      </button>
    );
  },
);

CarouselNext.displayName = 'CarouselNext';
